import { useState, useEffect } from "react";
import {
  ShoppingBag,
  MapPin,
  Clock,
  Heart,
  CheckCircle,
  Sparkles,
  ArrowRight,
  Download,
} from "lucide-react";
import { CUSTOMER_APP_URL } from "@/config/appConfig";


const CustomerHero = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  // const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const benefits = [
    "Order food, groceries & everyday essentials",
    "Save up to 70% with Mystery Bags",
    "Fast, convenient delivery",
    "Support local restaurants & stores",
  ];

  return (
    <div className="relative min-h-screen bg-linear-to-br from-emerald-950 via-green-900 to-teal-950 overflow-hidden font-arimo">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-20 left-10 w-96 h-96 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse"
          style={{
            backgroundColor: "#fef3c7",
            transform: `translateY(${scrollY * 0.2}px)`,
          }}
        />
        <div
          className="absolute top-40 right-10 w-lg h-128 bg-orange-400 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse"
          style={{
            transform: `translateY(${scrollY * 0.3}px)`,
            animationDelay: "1s",
          }}
        />
        <div
          className="absolute -bottom-32 left-1/3 w-96 h-96 bg-amber-400 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse"
          style={{
            transform: `translateY(${scrollY * 0.15}px)`,
            animationDelay: "2s",
          }}
        />
      </div>

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            "linear-gradient(rgba(254, 243, 199, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(254, 243, 199, 0.1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[calc(100vh-8rem)]">
          {/* Left column - Text content */}
          <div
            className={`space-y-8 transform transition-all duration-1000 ${
              isVisible
                ? "translate-x-0 opacity-100"
                : "-translate-x-12 opacity-0"
            }`}
          >
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 backdrop-blur-sm px-5 py-2.5 rounded-full text-sm font-medium border-2"
              style={{
                backgroundColor: "rgba(254, 243, 199, 0.1)",
                borderColor: "rgba(254, 243, 199, 0.3)",
                color: "#fef3c7",
              }}
            >
              <ShoppingBag className="w-4 h-4" />
              <span>Everything You Need, Delivered</span>
            </div>

            {/* Main heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white">
              Everything You Need At
              <span
                className="block text-transparent bg-clip-text"
                style={{
                  backgroundImage: `linear-gradient(to right, #fef3c7, #fbbf24)`,
                  WebkitBackgroundClip: "text",
                }}
              >
                Amazing Prices
              </span>
            </h1>

            {/* Subheading */}
            <p
              className="text-xl leading-relaxed max-w-xl"
              style={{ color: "rgba(254, 243, 199, 0.8)" }}
            >
              Order from your favorite restaurants, shop groceries and everyday
              essentials, and discover exclusive Mystery Bags - all in one smart
              platform.
            </p>

            {/* Benefits checklist */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {benefits.map((benefit, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-3 transform transition-all duration-500 delay-${
                    idx * 100
                  } ${
                    isVisible
                      ? "translate-x-0 opacity-100"
                      : "-translate-x-4 opacity-0"
                  }`}
                >
                  <CheckCircle
                    className="w-5 h-5 shrink-0"
                    style={{ color: "#fef3c7" }}
                  />
                  <span
                    className="text-sm"
                    style={{ color: "rgba(254, 243, 199, 0.9)" }}
                  >
                    {benefit}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                className="group relative px-6 py-3 rounded-xl font-semibold text-base whitespace-nowrap shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                style={{
                  backgroundColor: "#fef3c7",
                  color: "#7c2d12",
                }}
                onClick={() => {
                  window.location.href = `${CUSTOMER_APP_URL}/customer`;
                }}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Login as Customer
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    backgroundColor: "#fde68a",
                  }}
                />
              </button>

              <button
                className="group relative px-6 py-3 rounded-xl font-semibold text-base whitespace-nowrap shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 overflow-hidden border-2"
                style={{
                  borderColor: "#fef3c7",
                  color: "#fef3c7",
                }}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Download App
                  <Download className="w-5 h-5 group-hover:translate-y-1 transition-transform" />
                </span>
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    backgroundColor: "rgba(254, 243, 199, 0.1)",
                  }}
                />
              </button>
            </div>
          </div>

          {/* Right column - Mobile App Mockup */}
          <div
            className={`relative transform transition-all duration-1000 delay-300 ${
              isVisible
                ? "translate-x-0 opacity-100"
                : "translate-x-12 opacity-0"
            }`}
          >
            <div className="relative max-w-[320px] mx-auto">
              {/* Main phone mockup */}
              <div
                className="relative rounded-[3rem] overflow-hidden shadow-2xl backdrop-blur-md border-8 transform hover:scale-105 transition-transform duration-500 mx-auto"
                style={{
                  backgroundColor: "rgba(254, 243, 199, 0.1)",
                  borderColor: "rgba(254, 243, 199, 0.2)",
                  maxWidth: "320px",
                  width: "100%",
                  aspectRatio: "9/19.5",
                }}
              >
                {/* Phone notch */}
                <div
                  className="absolute top-0 left-1/2 transform -translate-x-1/2 h-7 rounded-b-3xl z-10"
                  style={{
                    width: "40%",
                    backgroundColor: "#1f2937",
                  }}
                />

                {/* Screen content */}
                <div className="relative h-full bg-white p-4 pt-12">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-emerald-600" />
                      <div>
                        <div className="text-xs text-gray-500">
                          Your location
                        </div>
                        <div className="text-sm font-semibold text-gray-800">
                          Hyderabad, India
                        </div>
                      </div>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Heart className="w-4 h-4 text-emerald-600" />
                    </div>
                  </div>

                  {/* Search bar */}
                  <div className="relative mb-4">
                    <input
                      type="text"
                      placeholder="Search food, groceries & stores"
                      className="w-full pl-4 pr-10 py-3 rounded-xl bg-gray-100 text-sm"
                      disabled
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                  </div>

                  {/* Carousel cards */}
                  <div className="flex gap-3 mb-4 overflow-x-hidden">
                    <div
                      className="shrink-0 rounded-2xl overflow-hidden relative"
                      style={{ width: "45%" }}
                    >
                      <div className="absolute inset-0 bg-linear-to-br from-red-900 to-orange-700" />
                      <div className="relative p-4 h-32 flex flex-col justify-between">
                        <div className="text-white text-xs font-semibold">
                          Popular Meals
                        </div>
                        <div className="text-white text-xl font-bold">
                          Burger Combo
                        </div>
                      </div>
                    </div>
                    <div
                      className="shrink-0 rounded-2xl overflow-hidden relative"
                      style={{ width: "45%" }}
                    >
                      <div className="absolute inset-0 bg-linear-to-br from-green-700 to-emerald-600" />
                      <div className="relative p-4 h-32 flex flex-col justify-between">
                        <div className="text-white text-xs font-semibold">
                          Fresh Groceries
                        </div>
                        <div className="text-white text-xl font-bold">
                          Daily Essentials
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Restaurant card */}
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="flex gap-3 p-3">
                      <div className="w-20 h-20 rounded-xl bg-linear-to-br from-orange-200 to-amber-200 shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-1">
                          <div>
                            <div className="text-sm font-bold text-gray-800">
                              Fresh Bites Restaurant
                            </div>
                            <div className="text-xs text-gray-500">
                              Hyderabad
                            </div>
                          </div>
                          <Heart className="w-5 h-5 text-gray-300" />
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex items-center gap-1 bg-emerald-600 text-white text-xs px-2 py-0.5 rounded">
                            <span>★</span>
                            <span>4.5</span>
                          </div>
                          <span className="text-xs text-gray-500">1.2 km</span>
                          <span className="text-xs font-bold text-gray-800">
                            $150
                          </span>
                          <span className="text-xs text-gray-400 line-through">
                            $360
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <Clock className="w-3 h-3" />
                          <span>Food • Grocery • Mystery Bags</span>
                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Delivery & Pickup Available
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating notification cards */}
              <div
                className="hidden sm:block absolute left-[-45%] md:left-[-20%] top-[15%] bg-white rounded-xl shadow-2xl p-3 max-w-40 animate-pulse"
                style={{ animationDuration: "3s" }}
              >
                <div className="flex items-start gap-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{
                      background: `linear-gradient(135deg, #fef3c7, #fbbf24)`,
                    }}
                  >
                    <ShoppingBag className="w-4 h-4 text-amber-900" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-800">
                      Save Up To 70%
                    </div>
                    <div className="text-xs text-gray-600">Mystery Bags</div>
                  </div>
                </div>
              </div>

              <div
                className="hidden sm:block absolute right-[-45%] md:right-[-20%] bottom-[20%] bg-white rounded-xl shadow-2xl p-3 max-w-40 animate-pulse"
                style={{ animationDuration: "3s", animationDelay: "1.5s" }}
              >
                <div className="flex items-start gap-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{
                      background: `linear-gradient(135deg, #34d399, #10b981)`,
                    }}
                  >
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-800">
                      Fast Delivery
                    </div>
                    <div className="text-xs text-gray-600">
                      Within Your Area
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom decoration */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{
          background: `linear-gradient(to right, transparent, rgba(254, 243, 199, 0.2), transparent)`,
        }}
      />
    </div>
  );
};

export default CustomerHero;
